import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import useAuth from '../../hooks/global/useAuth';
import { Trans, useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import classnames from 'classnames';
import { useMemo } from 'react';
import CashbackToggle from '../checkout/CashbackToggle';

interface Props {
  subtotal: number;
  total: number;
  hideLoginPrompt?: boolean;
  className?: string;
  type?: 1 | 2;
}

const OrderBasketCashback = (props: Props) => {
  const { subtotal, total, hideLoginPrompt = false, className, type = 1 } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Cashback:OrderBasketCashback' });

  const { isLoggedIn } = useAuth();
  const { toggleLoginModal } = useAppMethods();
  const { hasCashbackProgram, calculateExpectedCashback } = useCashbackProgram();

  const expectedCashback = useMemo(() => calculateExpectedCashback(total), [total, calculateExpectedCashback]);

  const onLoginClick = () =>
    toggleLoginModal({
      openModal: true,
      onCloseRedirect: () => null,
    });

  const renderAvailableBalance = () => {
    if (!isLoggedIn && !hideLoginPrompt) {
      return (
        <button onClick={onLoginClick} className="underline text-brand-text-secondary text-sm font-semibold">
          {t('buttons.log_in')}
        </button>
      );
    }

    return <CashbackToggle subtotal={subtotal} />;
  };

  const renderContent = () => {
    switch (type) {
      case 2:
        return (
          expectedCashback > 0 && (
            <div className={classnames('w-full bg-brand-primary/30 rounded-lg px-sm py-xs', className)}>
              <p className="text-sm text-center text-brand-primary">
                <Trans
                  t={t}
                  values={{ value: expectedCashback }}
                  components={{
                    highlight: <span className="text-brand-primary font-semibold" />,
                  }}
                >
                  sections.expected_cashback
                </Trans>
              </p>
            </div>
          )
        );

      default:
        return (
          <div className={classnames('w-full', className)}>
            {renderAvailableBalance()}
            {expectedCashback > 0 && (
              <p className="text-sm mt-xs">
                <Trans
                  t={t}
                  values={{ value: expectedCashback }}
                  components={{
                    highlight: <span className="text-brand-primary font-semibold" />,
                  }}
                >
                  sections.expected_cashback
                </Trans>
              </p>
            )}
          </div>
        );
    }
  };

  return hasCashbackProgram && renderContent();
};

export default OrderBasketCashback;

OrderBasketCashback.ExpectedCashback = ({ total, pointsHighlight }: { total: number; pointsHighlight?: string }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Cashback:OrderBasketCashback' });
  const { calculateExpectedCashback } = useCashbackProgram();

  const expectedCashback = useMemo(() => calculateExpectedCashback(total), [total, calculateExpectedCashback]);

  return expectedCashback > 0 ? (
    <Trans
      t={t}
      values={{ value: expectedCashback }}
      components={{
        highlight: <span className={classnames('text-brand-primary font-semibold', pointsHighlight)} />,
      }}
    >
      sections.expected_cashback
    </Trans>
  ) : null;
};
